import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Send, UserCheck, Zap } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { runAiJob } from '../../lib/aiJob';
import { MicButton, useSpeaker } from '../voice/VoiceControls';
import { StageMap, StageExplain } from './TrialStageMap';
import { TrialVerdict } from './TrialVerdict';
import { JUDGE, OPP } from './TrialSetup';
import { ModelAnswer } from './TrialModelAnswer';

const ROLE = { juez: ['Juez', 'border-amber-500/40 bg-amber-500/5'], contraparte: ['Abogado contrario', 'border-red-500/40 bg-red-500/5'], abogado: ['Tú', 'border-sky-500/40 bg-sky-500/10 ml-8'] };

export const people = (s) => ({ [s.user_id]: s.owner_name || 'Titular', ...Object.fromEntries((s.team || []).map((m) => [m.user_id, m.name])) });
export const assigneeOf = (s, i) => (s.assignments || {})[String(i)] || s.user_id;

const Transcript = ({ turns, me }) => {
  const box = useRef(null);
  useEffect(() => { if (box.current) box.current.scrollTop = box.current.scrollHeight; }, [turns.length]);
  return (
    <div ref={box} className="mt-2 max-h-72 space-y-2 overflow-y-auto rounded-lg border-t border-slate-800 pt-2 pr-1" data-testid="trial-transcript">
      {turns.map((t, i) => (
        <div key={i} data-testid={t.surprise ? 'trial-surprise-turn' : undefined} className={`rounded-lg border p-2.5 text-xs ${t.surprise ? 'border-red-500 bg-red-500/10' : ROLE[t.role][1]}`}>
          <p className="flex items-center gap-1.5 font-semibold text-white">{t.surprise && <Zap className="h-3.5 w-3.5 text-red-400" />}{t.role === 'abogado' && t.author && t.author !== me ? t.author : ROLE[t.role][0]}{t.surprise && <span className="text-red-300">· incidente sorpresa</span>}</p>
          <p className="mt-0.5 text-slate-200 whitespace-pre-line">{t.text}</p>
        </div>
      ))}
    </div>
  );
};

// Stage-by-stage play surface shared by the case dialog and the team page
export const TrialBoard = ({ s, setS, sidebarExtra }) => {
  const { api, user } = useAuth();
  const speak = useSpeaker();
  const [sel, setSel] = useState(s.current);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState('');
  const [voiceOn, setVoiceOn] = useState(false);
  const [mapOpen, setMapOpen] = useState(false);
  useEffect(() => { setSel(s.current); }, [s.id, s.current]);
  const job = async (label, path, body) => {
    setBusy(label);
    try { const r = await runAiJob(api, path, body, { timeout: 300000 }); setS(r); return r; }
    catch (e) { toast.error(e.response?.data?.detail || e.message); return null; } finally { setBusy(''); }
  };
  const send = async () => {
    if (text.trim().length < 5) return toast.error('Escribe tu intervención');
    const r = await job('El juez y la contraparte responden...', `/trial-sim/${s.id}/respond`, { text });
    if (r) { setText(''); if (voiceOn) { const last = r.stages[r.current].turns.slice(-2).map((t) => t.text).join(' '); if (last) speak(last, true); } }
  };
  const st = s.stages[sel];
  const names = people(s);
  const team = (s.team || []).length > 0;
  const turnOf = assigneeOf(s, s.current);
  const myTurn = turnOf === user?.id;
  const playing = s.status === 'running' && sel === s.current;
  const waiting = s.status === 'running' && !myTurn;
  useEffect(() => {
    if (!waiting) return undefined;
    const t = setInterval(() => api.get(`/trial-sim/${s.id}`).then((r) => setS(r.data.session)).catch(() => {}), 8000);
    return () => clearInterval(t);
  }, [waiting, s.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="grid gap-4 md:grid-cols-[230px_1fr]">
      <div className="space-y-3">
        <button data-testid="trial-map-toggle" onClick={() => setMapOpen(!mapOpen)} className="md:hidden w-full rounded-lg border border-slate-700 py-1.5 text-xs text-slate-300">{mapOpen ? 'Ocultar mapa del proceso' : `Ver mapa del proceso (${s.stages.length} etapas)`}</button>
        <div className={mapOpen ? '' : 'hidden md:block'}><StageMap session={s} selected={sel} names={team ? names : null} onSelect={(i) => { setSel(i); setMapOpen(false); }} /></div>
        <label className="flex items-center gap-2 text-xs text-slate-400"><input data-testid="trial-voice-toggle" type="checkbox" checked={voiceOn} onChange={(e) => { setVoiceOn(e.target.checked); if (!e.target.checked) speak.stop(); }} />Escuchar al juez y la contraparte</label>
        {s.profiles && (
          <div data-testid="trial-profiles" className="space-y-1 rounded-lg border border-slate-800 p-2 text-[11px]">
            <p className="text-slate-400">Juez: <span data-testid="trial-profile-judge" className="text-amber-300">{JUDGE[s.profiles.judge.tipo]?.[0]}</span>{s.profiles.judge.source === 'juez_real' && <span className="text-sky-300"> · juez real</span>}</p>
            <p className="text-slate-400">Contraparte: <span data-testid="trial-profile-opponent" className="text-red-300">{OPP[s.profiles.opponent.tipo]?.[0]}</span></p>
          </div>
        )}
        {sidebarExtra}
      </div>
      <div className="min-w-0 space-y-3">
        {s.verdict && sel === s.current && s.status === 'finished' ? <TrialVerdict session={s} /> : st && (
          <>
            <h3 className="text-sm font-semibold">{sel + 1}. {st.nombre} <span data-testid="trial-stage-rule" className="text-xs font-normal text-slate-400">· {st.plazo} · {st.norma}</span></h3>
            {st.surprise && <p data-testid="trial-surprise-banner" className="flex items-center gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-3 py-2 text-xs font-semibold text-red-200"><Zap className="h-4 w-4" />Incidente sorpresa: {st.surprise.titulo}</p>}
            <StageExplain st={st} />
            {st.turns.length > 0 && <Transcript turns={st.turns} me={names[user?.id]} />}
            {st.feedback && <p data-testid="trial-stage-feedback" className="rounded-lg bg-slate-900 p-2 text-xs text-slate-300"><span className="text-emerald-300 font-semibold">Evaluación {st.score}/10:</span> {st.feedback}</p>}
            {st.model_answer && <ModelAnswer key={sel} m={st.model_answer} stage={sel} />}
            {playing && team && (
              <p data-testid="trial-turn-banner" className={`flex items-center gap-2 rounded-lg px-3 py-2 text-xs ${myTurn ? 'bg-emerald-500/15 text-emerald-200' : 'bg-slate-800 text-slate-300'}`}>
                <UserCheck className="h-3.5 w-3.5" />{myTurn ? 'Es tu turno de argumentar en esta etapa' : `Turno de ${names[turnOf] || 'otro abogado'} · se actualiza sola cuando responda`}
              </p>
            )}
            {playing && myTurn && (
              <div className="flex gap-2">
                <textarea data-testid="trial-answer-input" value={text} onChange={(e) => setText(e.target.value)} rows={3} placeholder="Tu intervención como abogado..." className="flex-1 rounded-lg border border-slate-700 bg-slate-900 p-2 text-sm text-slate-100" />
                <div className="flex flex-col gap-2">
                  <MicButton testid="trial-mic-btn" disabled={!!busy} onText={(t) => setText((p) => (p ? `${p} ${t}` : t))} />
                  <button data-testid="trial-send-btn" onClick={send} disabled={!!busy} className="rounded-lg bg-amber-500 p-2 text-slate-950 disabled:opacity-60">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}</button>
                </div>
              </div>
            )}
            {busy && <p className="text-xs text-amber-200">{busy}</p>}
            {s.status === 'ready_for_verdict' && (
              <button data-testid="trial-verdict-btn" onClick={() => job('El tribunal dicta sentencia (≈1 min)...', `/trial-sim/${s.id}/verdict`, {})} disabled={!!busy} className="w-full rounded-lg bg-emerald-500 py-2 text-sm font-semibold text-slate-950 disabled:opacity-60">Escuchar la sentencia</button>
            )}
            {s.status === 'finished' && sel !== s.current && <button data-testid="trial-see-verdict" onClick={() => setSel(s.current)} className="text-xs text-amber-300 underline">Ver sentencia e informe final</button>}
          </>
        )}
      </div>
    </div>
  );
};
